import { Box, Flex, HStack, Input, InputGroup, InputLeftAddon, Switch, Text } from '@chakra-ui/react'

import { FormField } from '../../../../components/FormField'
import { FormSelect, SearchableSelect } from '../../../../components/Select'
import { slugify, WEBSITE_URL } from '../../../../helpers'
import { CONTEST_LINK_TYPE_LABELS, TContestLinkType } from '../../../../services/Contests/interfaces'
import { ISelectCourse } from '../../../../services/CoursesService/interfaces'
import { SelectTeacher } from '../../../../services/Teacher/types'
import { IContestFormTabProps } from './types'

function formatCourseLabel(course: ISelectCourse): string {
  const formats: string[] = []
  if (course.in_person) formats.push('Presencial')
  if (course.live_online) formats.push('Online Ao Vivo')
  if (course.recorded) formats.push('Online Gravado')
  if (formats.length === 0) return course.name
  return `${course.name} - ${formats.join(' / ')}`
}

interface IProps extends IContestFormTabProps {
  courses: ISelectCourse[]
  teachers: SelectTeacher[]
}

export function GeneralTab({ form, setForm, errors, saving, courses, teachers }: IProps) {
  return (
    <Flex direction="column" gap={3}>
      <Box>
        <FormField label="Nome" htmlFor="name" error={errors.name} isRequired>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            id="name"
            disabled={saving}
            maxLength={255}
          />
        </FormField>
      </Box>

      <Box>
        <FormField label="Tipo de link" htmlFor="link_type" error={errors.link_type} isRequired>
          <FormSelect
            id="link_type"
            isDisabled={saving}
            value={form.link_type ?? 'concurso'}
            onChange={(value) => setForm({ ...form, link_type: value as TContestLinkType })}
            options={Object.entries(CONTEST_LINK_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
            hideEmptyOption
          />
        </FormField>
      </Box>

      <Box>
        <FormField label="URL" htmlFor="slug" error={errors.slug} isRequired>
          <InputGroup>
            <InputLeftAddon>{`${WEBSITE_URL}/${form.link_type ?? 'concurso'}/`}</InputLeftAddon>
            <Input
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
              id="slug"
              disabled={saving}
              maxLength={255}
            />
          </InputGroup>
        </FormField>
      </Box>

      <Box>
        <FormField label="Cursos vinculados ao concurso" error={errors.courses_ids}>
          <SearchableSelect<number>
            isMulti
            value={form.courses_ids}
            options={courses.map((course) => ({ label: formatCourseLabel(course), value: course.id }))}
            onChange={(coursesIds) => setForm({ ...form, courses_ids: coursesIds })}
            placeholder="Selecione os cursos vinculados ao concurso"
            entity="curso"
            isDisabled={saving}
          />
        </FormField>
      </Box>

      <Box>
        <FormField label="Professores vinculados ao concurso" error={errors.teachers_ids}>
          <SearchableSelect<number>
            isMulti
            value={form.teachers_ids}
            options={teachers.map((teacher) => ({ label: teacher.name, value: teacher.id }))}
            onChange={(teachersIds) => setForm({ ...form, teachers_ids: teachersIds })}
            placeholder="Selecione os professores vinculados ao concurso"
            entity="professor"
            isDisabled={saving}
          />
        </FormField>
      </Box>

      {/* Kids contests are usually filled in by a parent with their own data;
          the flag makes the site registration form ask for the student's. */}
      <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
        <HStack justify="space-between" align="center">
          <FormField label="Exposição/concurso infantil" htmlFor="is_kids_switch" mb={0}>
            <Switch
              id="is_kids_switch"
              isChecked={form.is_kids ?? false}
              isDisabled={saving}
              onChange={(e) => setForm({ ...form, is_kids: e.target.checked })}
            />
          </FormField>
        </HStack>
        <Text variant="caption" mt={1}>
          Quando ativado, o formulário de inscrição no site avisa que o nome, a foto e os dados devem ser do aluno, e
          não do pai, mãe ou responsável.
        </Text>
      </Box>

      <Box>
        <FormField
          label="Link do Grupo do WhatsApp"
          htmlFor="whatsapp_group_link"
          error={errors.whatsapp_group_link}
          helperText="Opcional. O link será exibido ao participante na página de confirmação de pagamento."
        >
          <Input
            value={form.whatsapp_group_link ?? ''}
            onChange={(e) => setForm({ ...form, whatsapp_group_link: e.target.value || null })}
            type="url"
            id="whatsapp_group_link"
            placeholder="https://chat.whatsapp.com/..."
            disabled={saving}
            maxLength={500}
          />
        </FormField>
      </Box>
    </Flex>
  )
}
